// src/components/sourcex/useDialog.jsx
//
// Centered, in-page replacements for window.alert / confirm / prompt.
//
// WHY: the browser's native dialogs are anchored to the TOP of the window in
// Chrome, so on a SourceX page every confirmation appeared as a small strip
// pinned above the content while the thing it referred to sat in the middle of
// the screen. They also can't be styled, so they broke the glass aesthetic the
// rest of the page uses.
//
// The API deliberately mirrors the native one — same argument order, same
// return values (a confirm resolves true/false, a prompt resolves the string or
// null) — so a call site converts by adding `await` and nothing else. That
// keeps the diff at each site to one line and makes it obvious that behavior
// hasn't changed, only presentation.

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const overlayCls =
  "fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 font-[Manrope]";

/**
 * One dialog, rendered centered. Not exported — reach it through useDialog().
 */
function DialogView({ request, onResolve }) {
  const { kind, message, title, defaultValue, confirmLabel, cancelLabel, tone } =
    request;
  const [value, setValue] = useState(defaultValue ?? "");
  const inputRef = useRef(null);
  const okRef = useRef(null);

  // Reset the field whenever a new prompt opens, so a previous answer can't
  // leak into the next one.
  useEffect(() => {
    setValue(defaultValue ?? "");
  }, [defaultValue, request]);

  useEffect(() => {
    // Focus the input for a prompt, the confirm button otherwise, so Enter
    // does the obvious thing without reaching for the mouse.
    const t = setTimeout(() => {
      if (kind === "prompt") inputRef.current?.select();
      else okRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [kind]);

  const cancelValue = kind === "prompt" ? null : kind === "confirm" ? false : undefined;
  const acceptValue = kind === "prompt" ? value : kind === "confirm" ? true : undefined;

  // Escape always cancels; a native dialog does the same.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onResolve(cancelValue);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const danger = tone === "danger";

  return (
    <div
      className={overlayCls}
      role="dialog"
      aria-modal="true"
      // Clicking the backdrop cancels — but only the backdrop itself, never a
      // click that started inside the panel and drifted out.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onResolve(cancelValue);
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_24px_70px_-20px_rgba(124,58,237,0.6)] overflow-hidden">
        <div className="flex items-start gap-2.5 px-4 pt-4">
          {danger && (
            <AlertTriangle className="w-5 h-5 shrink-0 text-rose-300 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            {title && (
              <h3 className="text-sm font-semibold text-white leading-snug">
                {title}
              </h3>
            )}
            {message && (
              // whitespace-pre-line: these messages were written for a native
              // dialog and use newlines to separate a list of consequences.
              <p
                className={`text-sm text-slate-300 whitespace-pre-line leading-relaxed ${
                  title ? "mt-1" : ""
                }`}
              >
                {message}
              </p>
            )}
          </div>
          <button
            onClick={() => onResolve(cancelValue)}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {kind === "prompt" && (
          <div className="px-4 pt-3">
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onResolve(value);
                }
              }}
              className="w-full bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:border-purple-300/60 transition"
              placeholder="Type here…"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 px-4 py-3 mt-3 border-t border-white/10 bg-white/[0.02]">
          {kind !== "alert" && (
            <button
              onClick={() => onResolve(cancelValue)}
              className="px-3 py-2 rounded-xl text-sm text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] transition"
            >
              {cancelLabel || "Cancel"}
            </button>
          )}
          <button
            ref={okRef}
            onClick={() => onResolve(acceptValue)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium text-white shadow transition hover:brightness-110 ${
              danger
                ? "bg-gradient-to-r from-rose-500 to-red-500"
                : "bg-gradient-to-r from-purple-600 to-blue-600"
            }`}
          >
            {confirmLabel || (kind === "alert" ? "OK" : "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Centered dialogs for a page.
 *
 * @returns {{
 *   alert: (message: string, opts?: object) => Promise<void>,
 *   confirm: (message: string, opts?: object) => Promise<boolean>,
 *   prompt: (message: string, defaultValue?: string, opts?: object) => Promise<string|null>,
 *   dialog: JSX.Element|null
 * }}
 *
 * Render `dialog` anywhere in the page — it positions itself. Options:
 * `{ title, confirmLabel, cancelLabel, tone: "danger" }`.
 */
export function useDialog() {
  const [request, setRequest] = useState(null);
  // Held in a ref as well, so resolving never depends on the state value the
  // handler happened to close over.
  const resolveRef = useRef(null);

  const open = useCallback(
    (next) =>
      new Promise((resolve) => {
        // A second dialog while one is open would strand the first promise and
        // hang whatever awaited it; settle it at its cancel value first.
        if (resolveRef.current) resolveRef.current(undefined);
        resolveRef.current = resolve;
        setRequest(next);
      }),
    []
  );

  const handleResolve = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setRequest(null);
    if (resolve) resolve(value);
  }, []);

  const alert = useCallback(
    (message, opts = {}) => open({ kind: "alert", message, ...opts }),
    [open]
  );
  const confirm = useCallback(
    (message, opts = {}) => open({ kind: "confirm", message, ...opts }),
    [open]
  );
  const prompt = useCallback(
    (message, defaultValue = "", opts = {}) =>
      open({ kind: "prompt", message, defaultValue, ...opts }),
    [open]
  );

  const dialog = request ? (
    <DialogView request={request} onResolve={handleResolve} />
  ) : null;

  return { alert, confirm, prompt, dialog };
}

export default useDialog;
